import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  CircularProgress,
  MenuItem,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Chip,
  InputAdornment
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Dashboard as DashboardIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { getAuthHeader } from '../../services/authService';
import API_CONFIG from '../../config';
import * as XLSX from 'xlsx';
import categoryService from '../../services/categoryService';

const StockMatrixReport = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({
    categoryId: ''
  });
  const [searchQuery, setSearchQuery] = useState('');

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await categoryService.getCategoriesByCompany();
        setCategories(Array.isArray(data) ? data : data.content || []);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    loadCategories();
  }, []);

  // Fetch report data
  const fetchReportData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.categoryId) {
        params.categoryId = filters.categoryId;
      }

      const response = await axios.get(`${API_CONFIG.BASE_URL}/reports/stock-matrix`, {
        params,
        headers: getAuthHeader()
      });

      setReportData(response.data);
      enqueueSnackbar('Report loaded successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error fetching stock matrix report:', error);
      enqueueSnackbar('Failed to load report', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchReportData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter products by search query
  const filteredProducts = reportData?.products?.filter(product =>
    product.productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (product.sku && product.sku.toLowerCase().includes(searchQuery.toLowerCase()))
  ) || [];

  // Calculate total stock for a product
  const getTotalStock = (product) => {
    let total = product.warehouseStock || 0;
    if (product.locationStocks) {
      Object.values(product.locationStocks).forEach(qty => {
        total += qty || 0;
      });
    }
    return total;
  };

  // Export to Excel
  const handleExportToExcel = async () => {
    if (!reportData || !reportData.products || reportData.products.length === 0) {
      enqueueSnackbar('No data to export', { variant: 'warning' });
      return;
    }

    try {
      setIsExporting(true);

      const { locations, products } = reportData;

      // Build Excel structure
      const excelData = products.map((product, index) => {
        const row = {
          'S.No': index + 1,
          'Description': product.productName,
          'Volume': product.unit || 'PCS',
          'Selling Price': product.sellingPrice || 0
        };

        // Add location columns dynamically
        locations.forEach(location => {
          const stockKey = `location_${location.locationId}`;
          row[location.locationName] = product.locationStocks[stockKey] || 0;
        });

        // Add Godown/Warehouse column
        row['Godown'] = product.warehouseStock || 0;

        // Add Total column
        row['Total Stock'] = getTotalStock(product);

        return row;
      });

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Calculate range
      const range = XLSX.utils.decode_range(ws['!ref']);

      // Set column widths
      const columnWidths = [
        { wch: 8 },   // S.No
        { wch: 40 },  // Description
        { wch: 12 },  // Volume
        { wch: 15 },  // Selling Price
      ];

      // Add widths for location columns
      locations.forEach(() => {
        columnWidths.push({ wch: 15 });
      });
      columnWidths.push({ wch: 15 }); // Godown
      columnWidths.push({ wch: 15 }); // Total

      ws['!cols'] = columnWidths;

      // Style headers (row 0)
      for (let col = 0; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (!ws[cellAddress]) continue;

        ws[cellAddress].s = {
          font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
          fill: { fgColor: { rgb: "4472C4" } },
          alignment: { horizontal: "center", vertical: "center" },
          border: {
            top: { style: "thin", color: { rgb: "000000" } },
            bottom: { style: "thin", color: { rgb: "000000" } },
            left: { style: "thin", color: { rgb: "000000" } },
            right: { style: "thin", color: { rgb: "000000" } }
          }
        };
      }

      // Style data cells
      for (let row = 1; row <= range.e.r; row++) {
        for (let col = 0; col <= range.e.c; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          if (!ws[cellAddress]) continue;

          const cellStyle = {
            alignment: {
              horizontal: col === 1 ? "left" : "center",
              vertical: "center"
            },
            border: {
              top: { style: "thin", color: { rgb: "D3D3D3" } },
              bottom: { style: "thin", color: { rgb: "D3D3D3" } },
              left: { style: "thin", color: { rgb: "D3D3D3" } },
              right: { style: "thin", color: { rgb: "D3D3D3" } }
            }
          };

          // Number formatting
          if (col === 3) {
            // Selling Price column
            cellStyle.numFmt = '"₹"#,##0.00';
          } else if (col > 3) {
            // Stock columns
            cellStyle.numFmt = '#,##0.##';
            // Highlight zero or empty stock
            const cellValue = ws[cellAddress].v;
            if (!cellValue || cellValue === 0) {
              cellStyle.fill = { fgColor: { rgb: "FFF2CC" } };
            }
          }

          ws[cellAddress].s = cellStyle;
        }
      }

      // Freeze first row (headers)
      ws['!freeze'] = { xSplit: 0, ySplit: 1 };

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Stock Matrix');

      // Export
      const fileName = `stock_matrix_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      enqueueSnackbar('Report exported successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error exporting report:', error);
      enqueueSnackbar('Failed to export report', { variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DashboardIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Stock Matrix Report
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              color="info"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/reports/stock')}
              sx={{
                minWidth: 140,
                fontWeight: 600,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                }
              }}
            >
              Stock Report
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchReportData}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
              onClick={handleExportToExcel}
              disabled={isExporting || !reportData || loading}
            >
              {isExporting ? 'Exporting...' : 'Export to Excel'}
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Category"
                  value={filters.categoryId}
                  onChange={(e) => setFilters({ ...filters, categoryId: e.target.value })}
                  size="small"
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={fetchReportData}
                  disabled={loading}
                  sx={{ height: 40 }}
                >
                  Apply Filters
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        {reportData && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Total Products
                  </Typography>
                  <Typography variant="h4" color="primary.main">
                    {reportData.totalProducts || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Total Locations
                  </Typography>
                  <Typography variant="h4" color="secondary.main">
                    {reportData.totalLocations || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Displayed Products
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {filteredProducts.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    Total Items
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {filteredProducts.reduce((sum, p) => sum + getTotalStock(p), 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Report Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 5 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading report...</Typography>
            </Box>
          ) : !reportData ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Alert severity="info">No data available. Click Refresh to load report.</Alert>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 'calc(100vh - 450px)' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: 'white', minWidth: 60 }}>
                      S.No
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: 'white', minWidth: 250 }}>
                      Product Description
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: 'white', minWidth: 100 }} align="center">
                      Volume
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: 'white', minWidth: 120 }} align="center">
                      Selling Price
                    </TableCell>
                    {reportData.locations.map((location) => (
                      <TableCell
                        key={location.locationId}
                        sx={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: 'white', minWidth: 120 }}
                        align="center"
                      >
                        {location.locationName}
                      </TableCell>
                    ))}
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: 'white', minWidth: 120 }} align="center">
                      Godown
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f57c00', color: 'white', minWidth: 120 }} align="center">
                      Total Stock
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredProducts.map((product, index) => {
                    const totalStock = getTotalStock(product);
                    return (
                      <TableRow key={product.productId} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {product.productName}
                          </Typography>
                          {product.sku && (
                            <Typography variant="caption" color="text.secondary">
                              SKU: {product.sku}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={product.unit || 'PCS'} size="small" color="primary" variant="outlined" />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                            {formatCurrency(product.sellingPrice)}
                          </Typography>
                        </TableCell>
                        {reportData.locations.map((location) => {
                          const stockKey = `location_${location.locationId}`;
                          const qty = product.locationStocks[stockKey] || 0;
                          return (
                            <TableCell key={location.locationId} align="center">
                              {qty > 0 ? (
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {qty}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.disabled">
                                  -
                                </Typography>
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell align="center">
                          {product.warehouseStock > 0 ? (
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {product.warehouseStock}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ backgroundColor: '#fff3e0' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: totalStock > 0 ? 'success.main' : 'error.main' }}>
                            {totalStock}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {reportData && filteredProducts.length === 0 && !loading && (
          <Box sx={{ mt: 2 }}>
            <Alert severity="info">
              No products found matching your search criteria.
            </Alert>
          </Box>
        )}
      </Box>
    </Layout>
  );
};

export default StockMatrixReport;

